/**
 * v4Gate1Decision: the pure decision behind "v4 gate 1: Mission
 * (read_objective)" in the Dashboard v4 status cascade.
 *
 * EXTRACTED VERBATIM (2026-08-11, step 1 of the credential plan-progression
 * hotfix). The full Dashboard cannot be mounted under CRA Jest (documented ESM
 * wall), so the gate's decision is lifted into this pure helper and the effect
 * body becomes a thin dispatcher of the returned decision. Tests exercise THIS
 * imported production helper, never a transcription of it.
 *
 * The `!readObjective || readObjective === null` condition is preserved exactly
 * as the inline gate wrote it. The second clause is redundant (null is already
 * falsy) but is kept so the extraction is a literal move rather than a rewrite.
 *
 * CREDENTIAL JOURNEYS (2026-08-11 production hotfix). Credential challenges
 * never write capsule_user.read_objective: the T10 open-mission clearance is
 * the credential gate, and deriveCredentialEffectiveStatus exists precisely
 * because this gate would otherwise pin a credential student at status 1
 * forever. That left the gate firing on every render for credential cohorts and
 * dispatching updateIsTaskCreated(false), clobbering the server truth that the
 * student had already promoted a task. The student was stuck on Plan, and the
 * recovery paths that briefly restored the flag were reset again on the next
 * render.
 *
 * So for a credential journey this gate:
 *   - NEVER requests a task-state reset, in any render or either act. The
 *     level-scoped reset moved to the transition that owns it: the
 *     updateMissionCapsuleId reducer clears isTaskCreated when it advances the
 *     capsule (src/store/user/slice.js).
 *   - ALWAYS pins status 1 and returns early, so execution can never fall
 *     through into stock gates 2 to 7. That keeps the credential
 *     effective-status mirror the sole owner of downstream credential
 *     progression, which is what the Act-1 status contract requires.
 *
 * Non-credential cohorts are unaffected: their branch below is unchanged.
 *
 * @param {object} args
 * @param {boolean} args.isCredentialJourney journey variant is `credential`
 * @param {*} args.readObjective capsule_user.read_objective for the current capsule
 * @param {*} args.missionIsTaskCreated mission.isTaskCreated from the user slice
 * @returns {{resetTaskCreated: boolean, pinStatusOne: boolean, earlyReturn: boolean}}
 */
export function v4Gate1Decision({
  isCredentialJourney,
  readObjective,
  missionIsTaskCreated,
} = {}) {
  if (isCredentialJourney) {
    return { resetTaskCreated: false, pinStatusOne: true, earlyReturn: true };
  }
  if (!readObjective || readObjective === null) {
    return {
      // The inline gate dispatched updateIsTaskCreated(false) only when the
      // mission already reported a created task, so the reset is a truthy
      // check on that value, not a strict comparison.
      resetTaskCreated: !!missionIsTaskCreated,
      pinStatusOne: true,
      earlyReturn: true,
    };
  }
  return { resetTaskCreated: false, pinStatusOne: false, earlyReturn: false };
}

export default v4Gate1Decision;
